/**
 * Data hooks for §8 email templates — the library screen and, eventually, any
 * composer that wants to pick a template.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as api from '../../api/emailTemplates.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} [params] e.g. `{ folder, q, limit }` */
export function useTemplates(params = {}) {
  return useQuery({
    queryKey: queryKeys.emailTemplates.list(params),
    queryFn: () => api.listTemplates(params),
    placeholderData: (previous) => previous,
  });
}

export function useTemplateFolders() {
  return useQuery({
    queryKey: queryKeys.emailTemplates.folders,
    queryFn: () => api.listFolders(),
    staleTime: 60_000,
  });
}

export function useMergeFields() {
  return useQuery({
    queryKey: queryKeys.emailTemplates.mergeFields,
    queryFn: () => api.getMergeFields(),
    staleTime: Infinity,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => api.createTemplate(payload),
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.all });
      dispatch(pushToast({ tone: 'success', message: `Template "${template.name}" created.` }));
    },
    onError: (error) => {
      const dup = error?.code === 'template_name_in_use';
      dispatch(pushToast({
        tone: 'danger',
        message: dup ? 'Choose a unique template name.' : 'Could not create that template.',
        detail: dup ? undefined : error.message,
      }));
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => api.updateTemplate(id, patch, rowVersion),
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.all });
      dispatch(pushToast({ tone: 'success', message: `Template "${template.name}" saved.` }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      const dup = error?.code === 'template_name_in_use';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : dup ? 'Choose a unique template name.' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : undefined,
      }));
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => api.deleteTemplate(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.all });
      dispatch(pushToast({ tone: 'success', message: 'Template deleted.' }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not delete that template',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useDuplicateTemplate() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, name }) => api.duplicateTemplate(id, name),
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.all });
      dispatch(pushToast({ tone: 'success', message: `Cloned as "${template.name}".` }));
    },
    onError: (error) => {
      const dup = error?.code === 'template_name_in_use';
      dispatch(pushToast({
        tone: 'danger',
        message: dup ? 'Choose a unique template name.' : 'Could not clone that template.',
        detail: dup ? undefined : error.message,
      }));
    },
  });
}

/** Resolve tokens for the live preview. Not cached — the editor's own draft changes on every keystroke. */
export function usePreviewEmail() {
  return useMutation({ mutationFn: (body) => api.previewEmail(body) });
}

/** Enqueue a send from any composer — the caller supplies `context` (the activity anchor). */
export function useSendEmail() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.sendEmail(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'sent'] });
      dispatch(pushToast({ tone: 'success', message: 'Email queued to send.' }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not send that email', detail: error.message })),
  });
}

/** The Email screen's inbox list. @param {object} [params] e.g. `{ status, opened, q, limit }` */
export function useSentEmails(params = {}) {
  return useQuery({
    queryKey: queryKeys.email.sent(params),
    queryFn: () => api.listSentEmails(params),
    placeholderData: (previous) => previous,
  });
}
